import { Fragment } from 'react'
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import SectionHeader from '../SectionHeader'
import { MULTI_ROWS, MULTI_VERSIONS } from './resultsMockData'

/** All-metrics comparison table; the row leader's value is bold + coral. */
export default function MultiSummaryTable() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('results')

  const headers = [
    { label: t('multi.summary.metric') },
    { label: t('multi.summary.versionA'), letter: 'A', colorKey: MULTI_VERSIONS[0].colorKey },
    { label: t('multi.summary.versionB'), letter: 'B', colorKey: MULTI_VERSIONS[1].colorKey },
    { label: t('multi.summary.versionC'), letter: 'C', colorKey: MULTI_VERSIONS[2].colorKey },
  ]

  const cellSx = {
    py: 1.5,
    borderBottom: `1px solid ${c.ink08}`,
    fontSize: 13.5,
    color: c.ink,
    display: 'flex',
    alignItems: 'center',
    fontVariantNumeric: 'tabular-nums' as const,
  }

  const valueColor = (isLeader: boolean) => (isLeader ? c.coral : c.ink72)

  return (
    <Box sx={{ bgcolor: c.white, border: `1px solid ${c.ink08}`, borderRadius: '18px', p: 2.75, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <SectionHeader title={t('multi.summary.title')} subtitle={t('multi.summary.sub')} />
      <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', columnGap: 2, minWidth: 480 }}>
        {headers.map((h, i) => (
          <Box
            key={i}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: c.ink48,
              py: 1.25,
              borderBottom: `1px solid ${c.ink08}`,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {i > 0 && (
              <Box
                component="span"
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '4px',
                  bgcolor: theme.affexy.emotion[h.colorKey!],
                  color: '#fff',
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: 9,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {h.letter}
              </Box>
            )}
            {h.label}
          </Box>
        ))}
        {MULTI_ROWS.map((r) => (
          <Fragment key={r.labelKey}>
            <Box sx={{ ...cellSx, fontWeight: 500 }}>{t(`multi.summary.${r.labelKey}`)}</Box>
            <Typography sx={{ ...cellSx, fontWeight: r.w === 'a' ? 700 : 400, color: valueColor(r.w === 'a') }}>{r.a}</Typography>
            <Typography sx={{ ...cellSx, fontWeight: r.w === 'b' ? 700 : 400, color: valueColor(r.w === 'b') }}>{r.b}</Typography>
            <Typography sx={{ ...cellSx, fontWeight: r.w === 'c' ? 700 : 400, color: valueColor(r.w === 'c') }}>{r.c}</Typography>
          </Fragment>
        ))}
      </Box>
      </Box>
    </Box>
  )
}
